import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Stack,
  HStack,
  Button,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import TypeaheadWidget from "../../../CommonComponents/TypeaheadWidget";
import ImageWidget from "../../../CommonComponents/ImageWidget";
import ImagePreview from "../../../components/ImagePreview";
import { getDeletedValues, removeKeysFromFormData } from "../../../util/helper";
import { getResearchCategoriesAPI } from "../../../services/ResearchCategories";
import {
  getThemeBasicData,
  updateThemeBasicData,
} from "../../../services/Theme";
import { fetchMasterlistSubTypes } from "../../../util/getMasterlistSubType";
import FormLoader from "../../../CommonComponents/FormLoader";
import {
  getApiErrorMessage,
  showErrorToast,
  showSuccessToast,
} from "../../../util/errorHandler";
import {
  homeCollageOrderSchema,
  homeCollageOrderUiSchema,
} from "../../../constants/homeCollage";
import { parseHomeCollageConflict } from "../../../util/homeCollageConflict";
import { useHomeCollageReassign } from "../../../hooks/useHomeCollageReassign";
import HomeCollageReassignDialog from "../../../CommonComponents/HomeCollageReassignDialog";
import {
  exploreCarouselOrderSchema,
  exploreCarouselOrderUiSchema,
} from "../../../constants/exploreCarousel";
import {
  exploreSearchHintOrderSchema,
  exploreSearchHintOrderUiSchema,
  exploreSearchHintKindSchema,
  exploreSearchHintKindUiSchema,
  useSearchHintPoolSize,
} from "../../../constants/exploreSearchHints";
// import SubmitButton from '../CommonComponents/SubmitButton';

/**
 * Trims each string in the array and removes empty/whitespace-only values.
 * Always returns an array so the API always receives a clean payload.
 * Scale note: O(n) — safe for any realistic AKA list size.
 * @param {Array<string>} arr
 * @returns {Array<string>}
 */
const cleanArray = (arr) =>
  (arr ?? []).map((v) => (v ?? "").trim()).filter((v) => v !== "");

const defaultValues = {
  aka: [],
  aka_typo_tolerance: [],
  keywords: [],
  has_scholarship: false,
  isExplore: false,
};

const Basic = () => {
  const { alias } = useParams();
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const [basicFieldData, setBasicFieldData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const homeCollageReassign = useHomeCollageReassign();
  const [showImagePreview, setShowImagePreview] = useState(false);
  const toast = useToast();
  // Re-renders once the live hint pool size (app_configs) is known.
  useSearchHintPoolSize();
  const previewEnabled = useSelector((state) => state.preview.isEnabled);

  // Function for create options
  const transformOptions = (data) => {
    return data.map((itemArray) => {
      // Convert the array of key-value pairs into a single object
      const entity = itemArray.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {});

      // Use the `alias` field for label and value
      return {
        label: entity.alias,
        value: entity.alias,
      };
    });
  };

  const fetchCategories = async (inputValue) => {
    const result = await getResearchCategoriesAPI();
    const transformedOptions = transformOptions(result);

    // This function return the options
    return transformedOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  const transFormData = (data) => ({
    ...data,
    meta_title: data?.meta_title ?? "",
    meta_description: data?.meta_description ?? "",
    image_alias_preview: data?.image_alias || null,
    has_scholarship: data?.has_scholarship ?? false,
    isExplore: data?.isExplore ?? false,
    // Default to [] when no value exists — no input rows rendered on load (PRD §2)
    aka: Array.isArray(data?.aka) ? data.aka : [],
    aka_typo_tolerance: Array.isArray(data?.aka_type_tolerance)
      ? data.aka_type_tolerance
      : [],
  });

  // const reTransFormData = (data) => ({
  //   ...data,
  //   ssb_image_alias: [data?.ssb_image_alias]
  //   });

  // Check if isExplore is true (not just if it exists)
  const hasIsExplore = useMemo(() => {
    const dataToCheck = previewEnabled ? combinedFormData : rightFormData;
    return dataToCheck?.isExplore === true;
  }, [combinedFormData, rightFormData, previewEnabled]);

  // Left Column Schema - Basic Fields
  const leftColumnSchema = useMemo(() => {
    const baseProperties = {
      alias: {
        type: "string",
        title: "Alias",
      },
      meta_title: {
        type: "string",
        title: "Meta Title",
      },
      meta_description: {
        type: "string",
        title: "Meta Description",
      },
      full_name: {
        type: "string",
        title: "Full Name",
      },
      name: {
        type: "string",
        title: "Name",
      },
      image_alias: {
        type: "string",
        title: "Image",
      },
      image_alias_preview: {
        type: ["string", "null"],
        title: "Image Preview",
      },
      subject: {
        type: "string",
        title: "Masterlist Type",
      },
      masterlist_type: {
        type: "string",
        title: "Masterlist Sub Type",
      },
      researchCategories: {
        type: "array",
        title: "Research Category",
        items: {
          type: "object",
          properties: {
            alias: {
              type: "string",
              title: "Alias",
            },
          },
        },
      },
    };

    // Add image field only if isExplore is true
    if (hasIsExplore) {
      // baseProperties["image"] = {
      //   "type": "string",
      //   "title": "Explore Image URL"
      // };
    }

    return {
      title: "Primary fields",
      description: "",
      type: "object",
      properties: baseProperties,
      required: ["alias", "full_name", "name", "subject", "masterlist_type"],
    };
  }, [hasIsExplore]);

  // Right Column Schema - Metadata Fields
  const rightColumnSchema = useMemo(() => {
    const baseProperties = {
      aka: {
        type: "array",
        title: "AKA",
        items: {
          type: "string",
        },
      },
      aka_typo_tolerance: {
        type: "array",
        title: "AKA Typo Tolerance",
        items: {
          type: "string",
        },
      },
      keywords: {
        type: "array",
        title: "Keywords",
        items: {
          type: "string",
        },
      },
      has_scholarship: {
        type: "boolean",
        title: "Has Scholarship",
        default: false,
      },
      isExplore: {
        type: "boolean",
        title: "Is Explore",
        default: false,
      },
    };

    // Add isExplore fields only if isExplore is true
    if (hasIsExplore) {
      baseProperties["display_name"] = {
        type: "string",
        title: "Display Name",
      };
      baseProperties["sort_name"] = {
        type: "string",
        title: "Sort Name",
      };
      baseProperties["route_type"] = {
        type: "string",
        title: "Route",
      };
      baseProperties["image"] = {
        type: "string",
        title: "Explore Image URL",
      };
      baseProperties["home_collage_order"] = homeCollageOrderSchema();
      baseProperties["explore_carousel_order"] = exploreCarouselOrderSchema();
      baseProperties["explore_search_hint_order"] =
        exploreSearchHintOrderSchema();
      baseProperties["explore_search_hint_kind"] =
        exploreSearchHintKindSchema();
    }

    const requiredFields = [];
    if (hasIsExplore) {
      requiredFields.push("route_type");
      requiredFields.push("image");
    }

    return {
      title: "",
      description: "",
      type: "object",
      properties: baseProperties,
      required: requiredFields,
      default: defaultValues,
    };
  }, [hasIsExplore]);

  // Combined Schema for Single Column (Preview Mode)
  const combinedSchema = useMemo(() => {
    const baseProperties = {
      alias: {
        type: "string",
        title: "Alias",
      },
      meta_title: {
        type: "string",
        title: "Meta Title",
      },
      meta_description: {
        type: "string",
        title: "Meta Description",
      },
      full_name: {
        type: "string",
        title: "Full Name",
      },
      name: {
        type: "string",
        title: "Name",
      },
      image_alias: {
        type: "string",
        title: "Image",
      },
      image_alias_preview: {
        type: ["string", "null"],
        title: "Image Preview",
      },
      subject: {
        type: "string",
        title: "Masterlist Type",
      },
      masterlist_type: {
        type: "string",
        title: "Masterlist Sub Type",
      },
      researchCategories: {
        type: "array",
        title: "Research Category",
        items: {
          type: "object",
          properties: {
            alias: {
              type: "string",
              title: "Alias",
            },
          },
        },
      },
      aka: {
        type: "array",
        title: "AKA",
        items: {
          type: "string",
        },
      },
      aka_typo_tolerance: {
        type: "array",
        title: "AKA Typo Tolerance",
        items: {
          type: "string",
        },
      },
      keywords: {
        type: "array",
        title: "Keywords",
        items: {
          type: "string",
        },
      },
      has_scholarship: {
        type: "boolean",
        title: "Has Scholarship",
        default: false,
      },
      isExplore: {
        type: "boolean",
        title: "Is Explore",
        default: false,
      },
    };

    // Add isExplore fields only if isExplore is true
    if (hasIsExplore) {
      baseProperties["display_name"] = {
        type: "string",
        title: "Display Name",
      };
      baseProperties["sort_name"] = {
        type: "string",
        title: "Sort Name",
      };
      baseProperties["route_type"] = {
        type: "string",
        title: "Route",
      };
      baseProperties["image"] = {
        type: "string",
        title: "Explore Image URL",
      };
      baseProperties["home_collage_order"] = homeCollageOrderSchema();
      baseProperties["explore_carousel_order"] = exploreCarouselOrderSchema();
      baseProperties["explore_search_hint_order"] =
        exploreSearchHintOrderSchema();
      baseProperties["explore_search_hint_kind"] =
        exploreSearchHintKindSchema();
    }

    const requiredFields = [
      "alias",
      "full_name",
      "name",
      "subject",
      "masterlist_type",
    ];
    if (hasIsExplore) {
      requiredFields.push("route_type");
      requiredFields.push("image");
    }

    return {
      title: "Primary fields",
      description: "",
      type: "object",
      properties: baseProperties,
      required: requiredFields,
      default: defaultValues,
    };
  }, [hasIsExplore]);

  // Left Column UI Schema
  const leftColumnUiSchema = useMemo(() => {
    const baseSchema = {
      "ui:submitButtonOptions": {
        norender: true,
      },
      "ui:style": {
        fontSize: "14px",
      },
      researchCategories: {
        items: {
          alias: {
            "ui:widget": "typeahead1",
          },
        },
      },
      image_alias_preview: {
        "ui:widget": ImageWidget,
        "ui:options": {
          "ui:emptyValue": null,
        },
      },
      full_name: { "ui:emptyValue": "" },
      name: { "ui:emptyValue": "" },
      alias: { "ui:emptyValue": "", "ui:readonly": true },
      meta_title: { "ui:emptyValue": "" },
      meta_description: { "ui:widget": "textarea", "ui:emptyValue": "" },
      image_alias: { "ui:emptyValue": "" },
      subject: { "ui:emptyValue": "" },
      masterlist_type: { "ui:widget": "typeahead3", "ui:emptyValue": "" },
    };

    // Add image field only if isExplore is true
    if (hasIsExplore) {
      baseSchema.image = { "ui:emptyValue": "" };
    }

    return (isSubmitting) => baseSchema;
  }, [hasIsExplore]);

  // Right Column UI Schema
  const rightColumnUiSchema = useMemo(() => {
    const baseSchema = {
      "ui:submitButtonOptions": {
        norender: true,
      },
      "ui:style": {
        fontSize: "14px",
      },
      keywords: { "ui:emptyValue": "" },
      aka_typo_tolerance: { "ui:emptyValue": "" },
      aka: { "ui:emptyValue": "" },
      has_scholarship: { "ui:emptyValue": false },
      isExplore: { "ui:emptyValue": false },
    };

    // Add isExplore fields only if isExplore is true
    if (hasIsExplore) {
      baseSchema.display_name = { "ui:emptyValue": "" };
      baseSchema.sort_name = { "ui:emptyValue": "" };
      baseSchema.route_type = { "ui:emptyValue": "" };
      baseSchema.image = { "ui:emptyValue": "" };
      baseSchema.home_collage_order = homeCollageOrderUiSchema();
      baseSchema.explore_carousel_order = exploreCarouselOrderUiSchema();
      baseSchema.explore_search_hint_order = exploreSearchHintOrderUiSchema();
      baseSchema.explore_search_hint_kind = exploreSearchHintKindUiSchema();
    }

    return (isSubmitting) => baseSchema;
  }, [hasIsExplore]);

  // Combined UI Schema for Single Column (Preview Mode)
  const combinedUiSchema = useMemo(() => {
    const baseSchema = {
      "ui:submitButtonOptions": {
        norender: true,
      },
      "ui:style": {
        fontSize: "14px",
      },
      researchCategories: {
        items: {
          alias: {
            "ui:widget": "typeahead1",
          },
        },
      },
      image_alias_preview: {
        "ui:widget": ImageWidget,
        "ui:options": {
          "ui:emptyValue": null,
        },
      },
      full_name: { "ui:emptyValue": "" },
      name: { "ui:emptyValue": "" },
      alias: { "ui:emptyValue": "", "ui:readonly": true },
      meta_title: { "ui:emptyValue": "" },
      meta_description: { "ui:widget": "textarea", "ui:emptyValue": "" },
      image_alias: { "ui:emptyValue": "" },
      subject: { "ui:emptyValue": "" },
      masterlist_type: { "ui:widget": "typeahead3", "ui:emptyValue": "" },
      keywords: { "ui:emptyValue": "" },
      aka_typo_tolerance: { "ui:emptyValue": "" },
      aka: { "ui:emptyValue": "" },
      has_scholarship: { "ui:emptyValue": false },
      isExplore: { "ui:emptyValue": false },
    };

    // Add isExplore fields only if isExplore is true
    if (hasIsExplore) {
      baseSchema.display_name = { "ui:emptyValue": "" };
      baseSchema.sort_name = { "ui:emptyValue": "" };
      baseSchema.route_type = { "ui:emptyValue": "" };
      baseSchema.image = { "ui:widget": ImageWidget, "ui:emptyValue": "" };
      baseSchema.home_collage_order = homeCollageOrderUiSchema();
      baseSchema.explore_carousel_order = exploreCarouselOrderUiSchema();
      baseSchema.explore_search_hint_order = exploreSearchHintOrderUiSchema();
      baseSchema.explore_search_hint_kind = exploreSearchHintKindUiSchema();
    }

    return (isSubmitting) => baseSchema;
  }, [hasIsExplore]);

  const widgets = {
    typeahead1: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchCategories} />
    ),
    typeahead3: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchMasterlistSubTypes} />
    ),
  };

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "alias",
      "meta_title",
      "meta_description",
      "full_name",
      "name",
      "image_alias",
      "image_alias_preview",
      "subject",
      "masterlist_type",
      "researchCategories",
    ];
    const rightFields = [
      "aka",
      "aka_typo_tolerance",
      "keywords",
      "display_name",
      "sort_name",
      "route_type",
      "isExplore",
      "has_scholarship",
      "image",
      "home_collage_order",
      "explore_carousel_order",
      "explore_search_hint_order",
      "explore_search_hint_kind",
    ];

    const leftData = {};
    const rightData = {};

    // Process left fields with defaults
    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      } else {
        if (field === "image_alias_preview") {
          leftData[field] = null;
        } else if (field === "researchCategories") {
          leftData[field] = [];
        } else {
          leftData[field] = "";
        }
      }
    });

    // Process right fields with defaults
    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      } else {
        if (
          field === "aka" ||
          field === "aka_typo_tolerance" ||
          field === "keywords"
        ) {
          rightData[field] = [];
        } else if (field === "has_scholarship" || field === "isExplore") {
          rightData[field] = false;
        } else if (
          field === "home_collage_order" ||
          field === "explore_carousel_order" ||
          field === "explore_search_hint_order"
        ) {
          rightData[field] = null;
        } else {
          rightData[field] = "";
        }
      }
    });

    return { leftData, rightData };
  };

  // Combine data from both forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getThemeBasicData(alias);
          if (result) {
            const originalData = result;
            setBasicFieldData(originalData);
            const transformedData = transFormData(originalData);
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(transformedData);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(transformedData);
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty form data
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        console.error("Error fetching film data:", error);
        showErrorToast(toast, "Failed to fetch film data");
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
  }, [alias, toast, quickSave]);

  // Handle form changes for two-column layout
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Submit updated data
  const handleSubmit = async () => {
    // Use combined form data in preview mode, otherwise combine left and right forms
    const finalFormData = previewEnabled
      ? combinedFormData
      : combineFormData(leftFormData, rightFormData);

    // Clean AKA arrays before submission:
    // - Trim leading/trailing whitespace from each entry
    // - Remove empty/whitespace-only values
    // - Always send an array (even if empty) — never send empty strings (PRD §6)
    const cleanedFormData = {
      ...finalFormData,
      aka: cleanArray(finalFormData.aka),
      // Map form-state key back to API key before submission
      aka_type_tolerance: cleanArray(finalFormData.aka_typo_tolerance),
    };

    const keysToRemove = ["image_alias_preview", "aka_typo_tolerance"];

    // Remove extra fields added which are not available in DB
    const updatedFormData = removeKeysFromFormData(
      cleanedFormData,
      keysToRemove,
    );

    // Calculate deleted values for Neo4j - only for researchCategories field
    const allDeletedValues = getDeletedValues(basicFieldData, updatedFormData);

    const deletedValues = {};
    if (allDeletedValues.researchCategories) {
      deletedValues.researchCategories = allDeletedValues.researchCategories;
    }

    if (allDeletedValues.aka) {
      deletedValues.aka = allDeletedValues.aka;
    }
    if (allDeletedValues.aka_type_tolerance) {
      deletedValues.aka_type_tolerance = allDeletedValues.aka_type_tolerance;
    }

    // Shared by the initial attempt and the confirm-reassign retry below --
    // extraPayload merges in confirm_home_collage_order_reassign: true only
    // when the admin has explicitly confirmed taking a taken slot (#266343).
    const submitTheme = (extraPayload = {}) =>
      updateThemeBasicData(
        alias,
        { ...updatedFormData, ...extraPayload },
        deletedValues,
      );

    try {
      if (alias) {
        setIsSubmitting(true);
        const result = await submitTheme();

        if (result?.success) {
          setQuickSave((prev) => !prev);

          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      const { conflict } = parseHomeCollageConflict(error);
      if (conflict) {
        // Let the admin confirm taking the slot instead of a dead-end error.
        // Confirming calls this same retry, with the flag set, so the slot
        // swap happens atomically on the backend in one call (#266343).
        homeCollageReassign.open(conflict, async () => {
          try {
            const result = await submitTheme({
              confirm_home_collage_order_reassign: true,
            });
            if (result?.success) {
              setQuickSave((prev) => !prev);
              showSuccessToast(toast, "Data updated successfully");
            }
          } catch (retryError) {
            showErrorToast(
              toast,
              getApiErrorMessage(retryError, "Failed to update theme data"),
            );
            throw retryError;
          }
        });
      } else {
        console.error("Error updating theme data:", error);
        showErrorToast(
          toast,
          getApiErrorMessage(error, "Failed to update theme data"),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">
                    Primary fields
                  </Box>
                  {combinedFormData?.image_alias_preview && (
                    <IconButton
                      aria-label={
                        showImagePreview
                          ? "Hide image preview"
                          : "Show image preview"
                      }
                      icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                      size="sm"
                      colorScheme={showImagePreview ? "red" : "blue"}
                      onClick={() => setShowImagePreview((prev) => !prev)}
                      title={
                        showImagePreview
                          ? "Close image preview"
                          : "Open image preview"
                      }
                    />
                  )}
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema(isSubmitting)}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  widgets={widgets}
                  fontsize={"14px"}
                />
              </Box>
              {/* Third column for image preview when clicked */}
              {showImagePreview && combinedFormData?.image_alias_preview && (
                <HStack
                  sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                >
                  <ImagePreview image={combinedFormData?.image_alias_preview} />
                </HStack>
              )}
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">
                        Primary fields
                      </Box>
                      {leftFormData?.image_alias_preview && (
                        <IconButton
                          aria-label={
                            showImagePreview
                              ? "Hide image preview"
                              : "Show image preview"
                          }
                          icon={
                            showImagePreview ? <ViewOffIcon /> : <ViewIcon />
                          }
                          size="sm"
                          colorScheme={showImagePreview ? "red" : "blue"}
                          onClick={() => setShowImagePreview((prev) => !prev)}
                          title={
                            showImagePreview
                              ? "Close image preview"
                              : "Open image preview"
                          }
                        />
                      )}
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema(isSubmitting)}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema(isSubmitting)}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
              {/* Third column for image preview when clicked */}
              {showImagePreview && leftFormData?.image_alias_preview && (
                <HStack
                  sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                >
                  <ImagePreview image={leftFormData?.image_alias_preview} />
                </HStack>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button at Bottom */}
      <Box
        position="fixed"
        bottom={0}
        left="250px" // Account for sidebar width
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
        display="flex"
      >
        <Box maxW="auto" display="flex" mx="auto">
          <Button
            colorScheme="blue"
            size="lg"
            width="auto"
            isLoading={isSubmitting}
            loadingText="Saving..."
            onClick={() => handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>

      {/* Add padding to prevent content from being hidden behind fixed button */}
      <Box pb="100px" />

      <HomeCollageReassignDialog
        isOpen={homeCollageReassign.isOpen}
        conflict={homeCollageReassign.conflict}
        isConfirming={homeCollageReassign.isConfirming}
        cancelRef={homeCollageReassign.cancelRef}
        onConfirm={homeCollageReassign.confirm}
        onCancel={homeCollageReassign.close}
      />
    </Box>
  );
};

export default Basic;
